import { useState, useEffect } from 'react';
import { authApi } from '@/services/api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function LoginPage() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImg, setCaptchaImg] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/';

  const loadCaptcha = async () => {
    try {
      const { captchaId, imageBase64 } = await authApi.captcha();
      setCaptchaId(captchaId);
      setCaptchaImg(imageBase64);
      setCaptchaCode('');
    } catch {
      setError('验证码加载失败，请刷新');
    }
  };
  useEffect(() => { loadCaptcha(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone || !password || !captchaCode) {
      setError('请填写手机号、密码和验证码');
      return;
    }

    const result = await login(phone, password, captchaId, captchaCode);
    if (result.success) {
      navigate(returnUrl, { replace: true });
    } else {
      setError(result.error || '登录失败，请重试');
      loadCaptcha();
    }
  };

  return (
    <>
      <Navbar />
      <main className="w-full bg-background-50 min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="w-full max-w-md mx-auto px-4 py-10 md:py-14">
          <div className="bg-background-50 rounded-xl border border-background-200 p-6 md:p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
                <i className="ri-user-line text-xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mt-4">
                欢迎回来
              </h1>
              <p className="text-foreground-500 text-sm mt-1.5">
                登录您的绿证通账户
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-md flex items-start gap-2">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
                <p className="text-accent-700 text-xs">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="phone" className="block text-foreground-700 text-sm font-medium mb-1.5">
                  手机号
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="请输入注册手机号"
                  className="w-full bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  maxLength={11}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-foreground-700 text-sm font-medium mb-1.5">
                  密码
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                />
              </div>

              <div>
                <label htmlFor="captcha" className="block text-foreground-700 text-sm font-medium mb-1.5">
                  验证码
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id="captcha"
                    name="captcha"
                    type="text"
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder="请输入图中字符"
                    className="flex-1 bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                  {captchaImg && (
                    <img
                      src={captchaImg}
                      alt="验证码"
                      onClick={loadCaptcha}
                      title="点击刷新"
                      className="h-[42px] rounded-md border border-background-300 cursor-pointer"
                    />
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? '登录中...' : '登录'}
              </button>
            </form>

            <div className="mt-3 text-center">
              <Link to="/forgot-password" className="text-foreground-400 hover:text-primary-600 text-sm">
                忘记密码？
              </Link>
            </div>

            <div className="mt-6 text-center">
              <p className="text-foreground-400 text-sm">
                还没有账户？
                <Link
                  to={`/register${returnUrl !== '/' ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`}
                  className="text-primary-600 hover:text-primary-700 ml-1 font-medium cursor-pointer"
                >
                  立即注册
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}